/**
 * Draggable floating widgets — spec §F2 ("`.widget-head` (grab cursor, drag via
 * `lib/draggable.ts`)") and §F5.11 ("Every floating widget draggable within map bounds and
 * never off-screen").
 *
 * Port of the mockup's `makeDraggable(widget, handle)`: a pointerdown on the handle
 * switches the widget from its CSS anchor (`right`/`bottom`/`transform`) to absolute
 * `left`/`top` inside its containing block, then follows the pointer until pointerup.
 *
 * **Deviation from the mockup (NOTES #191):** the mockup clamps `y` to
 * `hr.height - 46`, i.e. it lets a widget hang off the bottom of the map as long as ~one
 * header row stays grabbable. §F5.11 says "never off-screen", so `clampPosition` keeps the
 * whole widget inside the container on both axes, and re-clamps on window resize (a
 * shrinking map must pull its widgets back in, which the mockup never did at all).
 *
 * **Touch (NOTES #198):** the mockup is mouse-only. Here the handle takes pointer capture
 * for the duration of the gesture and its CSS carries `touch-action: none`, without which
 * a touch drag is stolen by the browser's own pan/zoom after a few pixels and arrives as a
 * `pointercancel` — which ends the drag cleanly, but only ever *ends* it.
 */
import { useEffect, type RefObject } from 'react';

/** Gap kept between a widget and the container edge — the mockup's 2px. */
export const DRAG_PAD = 2;

export interface Size {
  width: number;
  height: number;
}

/** A widget box in **container** coordinates (not viewport coordinates). */
export interface Box extends Size {
  left: number;
  top: number;
}

export interface Position {
  left: number;
  top: number;
}

/**
 * The clamp maths, pulled out so it is unit-testable without a DOM.
 *
 * `rect` is the widget's desired position/size relative to `containerRect`'s top-left.
 * The result keeps the widget fully inside, `pad` px from every edge. A widget larger
 * than the container (or a container smaller than `2 * pad`) pins to the top-left corner
 * rather than producing an inverted range — that is the resize-shrink case, and losing the
 * bottom-right of an over-sized widget beats losing its header.
 */
export function clampPosition(rect: Box, containerRect: Size, pad: number = DRAG_PAD): Position {
  const maxLeft = containerRect.width - rect.width - pad;
  const maxTop = containerRect.height - rect.height - pad;
  return {
    left: Math.max(pad, Math.min(rect.left, maxLeft)),
    top: Math.max(pad, Math.min(rect.top, maxTop)),
  };
}

export interface DraggableOptions {
  /**
   * The element the widget must stay inside. Defaults to the widget's `offsetParent`,
   * which for every map widget is `#mapwrap` (the only positioned ancestor).
   */
  bounds?: RefObject<HTMLElement | null> | null;
  pad?: number;
  /** `false` leaves the element completely alone (no listeners, no inline position). */
  enabled?: boolean;
}

/** Interactive descendants must keep their own pointer behaviour (the mockup's guard). */
const INTERACTIVE = 'button, input, select, a, textarea, label';

/**
 * Make `ref` draggable by `handleRef`. Both may be the same element (the §F5.10 time bar
 * is dragged by its whole body, as in the mockup).
 *
 * **`enabled` is also the mount switch.** The effect reads `ref.current` once and its deps
 * are all stable, so a component that renders `null` until it opens (the §F6 dock) must
 * pass its open flag here — otherwise the effect runs at page load against a `null` element,
 * bails on the guard below, and never runs again once the DOM appears (NOTES #637).
 */
export function useDraggable<T extends HTMLElement, H extends HTMLElement>(
  ref: RefObject<T | null>,
  handleRef: RefObject<H | null>,
  options: DraggableOptions = {},
): void {
  const { bounds = null, pad = DRAG_PAD, enabled = true } = options;

  useEffect(() => {
    const el = ref.current;
    const handle = handleRef.current;
    if (!enabled || !el || !handle) return;

    const host = (): HTMLElement =>
      bounds?.current ?? (el.offsetParent as HTMLElement | null) ?? document.body;

    /**
     * Teardown for a gesture that is still in flight. The move/up listeners live on
     * `window`, not on the widget, so unmounting the component mid-drag (the §F5.7 profile
     * being closed by a country change while the user holds its header, a route change, an
     * indicator cleared from the sidebar) would otherwise leave them attached to a `window`
     * that outlives the element — a leak that keeps writing `style.left` on a detached
     * node for the rest of the session. `null` while no gesture is active.
     */
    let endGesture: (() => void) | null = null;

    const place = (left: number, top: number, hostRect: Size): void => {
      const p = clampPosition(
        { left, top, width: el.offsetWidth, height: el.offsetHeight },
        hostRect,
        pad,
      );
      el.style.left = `${p.left}px`;
      el.style.top = `${p.top}px`;
      el.style.right = 'auto';
      el.style.bottom = 'auto';
      el.style.transform = 'none';
    };

    const onPointerDown = (e: PointerEvent): void => {
      if (e.button !== 0) return;
      const target = e.target as Element | null;
      if (target?.closest(INTERACTIVE)) return;
      e.preventDefault();

      const hr = host().getBoundingClientRect();
      const wr = el.getBoundingClientRect();
      const offX = e.clientX - wr.left;
      const offY = e.clientY - wr.top;
      const pointerId = e.pointerId;
      // Freeze the current visual position as left/top before the first move, so a
      // `transform`-centred widget does not jump by half its width.
      place(wr.left - hr.left, wr.top - hr.top, hr);

      // Capture keeps the gesture attached to the handle when the pointer leaves it — over
      // the Leaflet canvas, out of the window, or (on touch) anywhere at all. The window
      // listeners below still fire, because captured events are dispatched to the handle
      // and bubble from there; capture only guarantees they are *not* retargeted or
      // swallowed by whatever the pointer happens to be over.
      try {
        handle.setPointerCapture?.(pointerId);
      } catch {
        /* capture is best-effort: the window listeners are the fallback */
      }

      const forThisPointer = (ev: PointerEvent): boolean =>
        ev.pointerId === undefined || ev.pointerId === pointerId;

      const move = (ev: PointerEvent): void => {
        if (!forThisPointer(ev)) return;
        place(ev.clientX - hr.left - offX, ev.clientY - hr.top - offY, hr);
      };
      // `pointercancel` (the browser took the gesture over — a scroll, a system gesture, a
      // lost touch) and `lostpointercapture` end the drag exactly like `pointerup`: leaving
      // the move listener attached would make the widget follow a pointer nobody is
      // pressing.
      const stop = (): void => {
        endGesture = null;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
        handle.removeEventListener('lostpointercapture', end);
        try {
          if (handle.hasPointerCapture?.(pointerId)) handle.releasePointerCapture?.(pointerId);
        } catch {
          /* already released with the pointer */
        }
      };
      const end = (ev: PointerEvent): void => {
        if (!forThisPointer(ev)) return;
        stop();
      };
      endGesture = stop;
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
      handle.addEventListener('lostpointercapture', end);
    };

    const onResize = (): void => {
      // Only a widget the user has actually moved gets re-clamped: an untouched one still
      // lives on its CSS anchor (`bottom:38px`, `left:50%` + translate) and writing
      // left/top would silently break that anchoring. The record of a move is the inline
      // `left` itself rather than a ref, so a widget that unmounts and mounts again — the
      // §F6 dock, which closes to nothing — comes back on its anchor, which is where it
      // visibly is.
      if (!el.style.left) return;
      const hr = host().getBoundingClientRect();
      const wr = el.getBoundingClientRect();
      place(wr.left - hr.left, wr.top - hr.top, hr);
    };

    handle.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('resize', onResize);
    return () => {
      handle.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', onResize);
      endGesture?.();
    };
  }, [ref, handleRef, bounds, pad, enabled]);
}
